import {
  Button,
  FormControl,
  MenuItem,
  Select,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { Link } from 'react-router-dom';
import { MatterStatus } from '~/gql/graphql';
import type { MatterViewModel } from '~/modules/matters/matters.types';
import { getMatterDisplayHeading } from '~/shared/utils/matterDisplay';
import {
  MatterStatusChip,
  getMatterStatusColor,
} from '~/modules/matters/MatterStatusChip';

interface Props {
  matter: MatterViewModel;
  isAttorney: boolean;
  isExpertOnMatter: boolean;
  isInvitedOnlyExpert: boolean;
  onStatusChange: (value: MatterStatus) => void;
  /** Shown to the right of the status dropdown for expert view (e.g. "Leave", "Decline"). */
  declineLinkLabel?: string;
  onOpenDecline?: () => void;
}

function isMatterStatus(value: unknown): value is MatterStatus {
  return (
    typeof value === 'string' &&
    (Object.values(MatterStatus) as string[]).includes(value)
  );
}

export const MatterDetailHeader = ({
  matter,
  isAttorney,
  isExpertOnMatter,
  isInvitedOnlyExpert,
  onStatusChange,
  declineLinkLabel,
  onOpenDecline,
}: Props) => {
  const theme = useTheme();
  const statusColors = getMatterStatusColor(theme, matter.status);
  // Invited-only experts see the matter as a "potential" engagement until they
  // accept, so we surface it as Inactive in their chip and strip the redundant
  // "(potential)" suffix from the heading.
  const displayStatus = isInvitedOnlyExpert
    ? MatterStatus.Inactive
    : matter.status;
  const showDeclineInHeader = Boolean(
    declineLinkLabel &&
      onOpenDecline &&
      (isExpertOnMatter || isInvitedOnlyExpert),
  );
  return (
    <>
      <BreadcrumbStack to="/matters">
        <ArrowBack sx={{ fontSize: 18 }} aria-hidden />
        Back to Matters
      </BreadcrumbStack>

      <HeaderStack>
        <TitleRowStack>
          <Typography variant="h1" fontWeight={700}>
            {getMatterDisplayHeading(matter, {
              stripPotentialLabel: displayStatus === MatterStatus.Inactive,
            })}
          </Typography>
          {isAttorney ? (
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <StyledMatterStatusSelect
                value={matter.status}
                onChange={(event) => {
                  const value = event.target.value;
                  if (isMatterStatus(value)) {
                    onStatusChange(value);
                  }
                }}
                displayEmpty
                $bgcolor={statusColors.bgcolor}
                $color={statusColors.color}
                inputProps={{ 'aria-label': 'Matter status' }}
              >
                <MenuItem value={MatterStatus.Active}>Active</MenuItem>
                <MenuItem value={MatterStatus.Inactive}>Inactive</MenuItem>
                <MenuItem value={MatterStatus.Closed}>Closed</MenuItem>
              </StyledMatterStatusSelect>
            </FormControl>
          ) : (
            <MatterStatusChip status={displayStatus} />
          )}
          {showDeclineInHeader && (
            <HeaderDeclineButton
              variant="outlined"
              size="small"
              onClick={onOpenDecline}
              aria-label={declineLinkLabel}
            >
              {declineLinkLabel}
            </HeaderDeclineButton>
          )}
        </TitleRowStack>
      </HeaderStack>
    </>
  );
};

const StyledMatterStatusSelect = styled(Select, {
  shouldForwardProp: (prop) => prop !== '$bgcolor' && prop !== '$color',
})<{ $bgcolor: string; $color: string }>(({ theme, $bgcolor, $color }) => ({
  height: 32,
  fontWeight: 600,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: $bgcolor,
  color: $color,
  '& .MuiSelect-select': {
    paddingTop: theme.spacing(0.75),
    paddingBottom: theme.spacing(0.75),
    paddingLeft: theme.spacing(1.25),
    paddingRight: theme.spacing(1.25),
    fontSize: theme.typography.caption.fontSize,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'transparent',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255,255,255,0.5)',
  },
}));

const BreadcrumbStack = styled(Link)(({ theme }) => ({
  display: 'inline-flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  marginBottom: theme.spacing(1),
  color: theme.palette.amount.main,
  textDecoration: 'underline',
  fontFamily: theme.typography.body2.fontFamily,
  fontSize: theme.typography.body2.fontSize,
  fontWeight: theme.typography.body2.fontWeight,
  lineHeight: theme.typography.body2.lineHeight,
  letterSpacing: theme.typography.body2.letterSpacing,
  '&:hover': {
    color: theme.palette.amount.dark,
  },
}));

const HeaderStack = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  flexWrap: 'wrap',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
}));

const TitleRowStack = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  flexWrap: 'wrap',
}));

const HeaderDeclineButton = styled(Button)(({ theme }) => ({
  borderColor: theme.palette.red.main,
  color: theme.palette.red.main,
  textTransform: 'none',
  fontWeight: 600,
  '&:hover': {
    borderColor: theme.palette.red.dark,
    color: theme.palette.red.dark,
    backgroundColor: theme.palette.action.hover,
  },
})) as typeof Button;
